import type { List } from 'app/generated/graphql'
import { useListStageEditor } from 'app/components/lists/board/useListStageEditor'
import type { ConfiguredStage } from 'app/components/lists/manual/useStageVisibility'
import EditPipeline from 'app/components/pipelines/EditPipeline'
import { showModal } from 'app/lib/modal'
import { logError } from 'lib/logging'
import { defaultStageIdOf } from 'lib/practiceLists'

const MODAL_OPTIONS = { isFullScreen: false, removePadding: true, useRadix: true } as const

/** The add and edit stage forms a list board opens, over the stage editor that saves them by id. */
export function useListStageModals({ list, onChanged }: { list: List; onChanged: () => void }) {
  const editor = useListStageEditor({ list, onChanged })
  const stages = list.stages ?? []

  const openAddStage = () =>
    showModal(
      <EditPipeline
        type="ADD"
        index={stages.length}
        updatePipelineStage={editor.updateStage}
        // The delete button never shows for an "ADD" form: nothing here ever calls this.
        deleteStage={() => {}}
        // A list's first stage is its default: every member without one starts there. A system
        // list's default is the system's, so the form offers no control for it.
        defaultStage={list.system ? undefined : { isDefault: stages.length === 0 }}
      />,
      { ...MODAL_OPTIONS, title: `Add stage` },
    )

  const openStageEditor = (stage: ConfiguredStage) => {
    // The stage's index in `list.stages`, never its index among the visible stages: a hidden
    // earlier stage would otherwise shift every visible one's index out from under it.
    const stageIndex = stages.findIndex((candidate) => candidate.id === stage.id)
    if (stageIndex === -1) {
      // An index of -1 means "add" to the editor: opening it on this would create a duplicate
      // instead of editing the stage that was clicked.
      logError(`Error opening a list's stage editor`, new Error(`Stage "${stage.name}" is not in the list's stages`))
      return
    }
    showModal(
      <EditPipeline
        type="UPDATE"
        stage={stage}
        index={stageIndex}
        updatePipelineStage={editor.updateStage}
        deleteStage={() => editor.deleteStage(stage.id!)}
        defaultStage={list.system ? undefined : { isDefault: stage.id === defaultStageIdOf(list) }}
      />,
      { ...MODAL_OPTIONS, title: `Edit stage` },
    )
  }

  return { openAddStage, openStageEditor }
}
